import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getPeriodOptions } from '@/lib/utils/period-options';

const defaultCollaborators = [];
const defaultSubsidiaries = [];

export const FORM_FOLDER_FILTER_ID = 'form-folder-filter';

/** Shared toggle-button classes */
const toggleBase = 'p-3 rounded-lg text-left transition-colors border cursor-pointer';
const toggleSelected = 'bg-secondary/20 border-secondary';
const toggleUnselected = 'bg-muted border-transparent';

export default function FormFolderFilter({
    onSubmit,
    filters = null,
    onReset,
    collaborators = defaultCollaborators,
    subsidiaries = defaultSubsidiaries,
}) {
    const { t } = useTranslation();
    const periods = getPeriodOptions();
    const collabList = useMemo(
        () => (Array.isArray(collaborators) ? collaborators.filter((c) => c.id !== 'all') : []),
        [collaborators],
    );
    const individualIds = useMemo(() => collabList.map((c) => c.id), [collabList]);

    // Build subsidiary options: "Mon entreprise" + each subsidiary
    const subsList = useMemo(() => {
        if (!Array.isArray(subsidiaries) || subsidiaries.length === 0) return [];
        return subsidiaries.map((s) => ({
            id: String(s.uniq_id ?? s.id),
            name: s.name,
        }));
    }, [subsidiaries]);
    const hasSubsidiaries = subsList.length > 0;

    // All entity IDs including "own"
    const allEntityIds = useMemo(() => ['own', ...subsList.map((s) => s.id)], [subsList]);

    const toCollabSet = useCallback(
        (filter) => {
            if (!filter || filter === 'all') return new Set(individualIds);
            if (Array.isArray(filter)) return new Set(filter);
            return new Set([filter]);
        },
        [individualIds],
    );

    const toEntitySet = useCallback(
        (filter) => {
            if (!filter || !Array.isArray(filter)) return new Set(allEntityIds);
            return new Set(filter);
        },
        [allEntityIds],
    );

    const [selectedCollabs, setSelectedCollabs] = useState(() => toCollabSet(filters?.collaborator));
    const [period, setPeriod] = useState(filters?.period || 'all');
    const [selectedEntities, setSelectedEntities] = useState(() => toEntitySet(filters?.subsidiaries));

    useEffect(() => {
        if (onReset) {
            setSelectedCollabs(new Set(individualIds));
            setPeriod('all');
            setSelectedEntities(new Set(allEntityIds));
        }
    }, [onReset, individualIds, allEntityIds]);

    useEffect(() => {
        if (filters) {
            setSelectedCollabs(toCollabSet(filters.collaborator));
            setPeriod(filters.period || 'all');
            setSelectedEntities(toEntitySet(filters.subsidiaries));
        }
    }, [filters, toCollabSet, toEntitySet]);

    const isAllCollabsSelected = individualIds.length > 0 && individualIds.every((id) => selectedCollabs.has(id));
    const isAllEntitiesSelected = allEntityIds.every((id) => selectedEntities.has(id));

    const handleToggleCollab = (id) => {
        setSelectedCollabs((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                if (next.size === 0) return new Set(individualIds);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAllCollabs = () => {
        if (isAllCollabsSelected) {
            setSelectedCollabs(new Set());
        } else {
            setSelectedCollabs(new Set(individualIds));
        }
    };

    const handleToggleEntity = (id) => {
        setSelectedEntities((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
                if (next.size === 0) return new Set(allEntityIds);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const handleSelectAllEntities = () => {
        if (isAllEntitiesSelected) {
            setSelectedEntities(new Set());
        } else {
            setSelectedEntities(new Set(allEntityIds));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const result = {
            collaborator: isAllCollabsSelected ? 'all' : Array.from(selectedCollabs),
            period,
        };
        if (hasSubsidiaries && !isAllEntitiesSelected) {
            result.subsidiaries = Array.from(selectedEntities);
        }
        onSubmit(result);
    };

    return (
        <form id={FORM_FOLDER_FILTER_ID} onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5">
            {/* ── Période ── */}
            <div className="mb-6">
                <h3 className="text-base text-foreground mb-3">{t('folders:periode')}</h3>
                <div className="grid grid-cols-3 gap-2.5">
                    {periods.map((p) => (
                        <button
                            key={p.id}
                            type="button"
                            onClick={() => setPeriod(p.id)}
                            className={`${toggleBase} text-center ${period === p.id ? toggleSelected : toggleUnselected}`}
                        >
                            <span className="text-[13px] text-foreground">{p.name}</span>
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Collaborateurs & Entreprises ── */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Collaborateurs */}
                <div className="mb-6 lg:mb-0">
                    <h3 className="text-base text-foreground mb-3">{t('folders:collaborateurs')}</h3>
                    <div className="space-y-2.5">
                        {/* Tous les collaborateurs */}
                        <button
                            type="button"
                            onClick={handleSelectAllCollabs}
                            className={`w-full ${toggleBase} ${isAllCollabsSelected ? toggleSelected : toggleUnselected}`}
                        >
                            <span className="text-[13px] text-foreground">{t('folders:tousLesCollaborateurs')}</span>
                        </button>

                        {collabList.map((collab) => {
                            const isActive = selectedCollabs.has(collab.id);
                            return (
                                <button
                                    key={collab.id}
                                    type="button"
                                    onClick={() => handleToggleCollab(collab.id)}
                                    className={`w-full ${toggleBase} ${isActive ? toggleSelected : toggleUnselected}`}
                                >
                                    <span className="text-[13px] text-foreground">{collab.name}</span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Entreprises (visible only when subsidiaries exist) */}
                {hasSubsidiaries && (
                    <div className="mb-6 lg:mb-0">
                        <h3 className="text-base text-foreground mb-3">{t('folders:entreprises')}</h3>
                        <div className="space-y-2.5">
                            {/* Toutes les entreprises */}
                            <button
                                type="button"
                                onClick={handleSelectAllEntities}
                                className={`w-full ${toggleBase} ${isAllEntitiesSelected ? toggleSelected : toggleUnselected}`}
                            >
                                <span className="text-[13px] text-foreground">{t('folders:toutesLesEntreprises')}</span>
                            </button>

                            {/* Mon entreprise */}
                            <button
                                type="button"
                                onClick={() => handleToggleEntity('own')}
                                className={`w-full ${toggleBase} ${selectedEntities.has('own') ? toggleSelected : toggleUnselected}`}
                            >
                                <span className="text-[13px] text-foreground">{t('folders:monEntreprise')}</span>
                            </button>

                            {/* Subsidiaries */}
                            {subsList.map((sub) => {
                                const isActive = selectedEntities.has(sub.id);
                                return (
                                    <button
                                        key={sub.id}
                                        type="button"
                                        onClick={() => handleToggleEntity(sub.id)}
                                        className={`w-full ${toggleBase} ${isActive ? toggleSelected : toggleUnselected}`}
                                    >
                                        <span className="text-[13px] text-foreground">{sub.name}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
}
